<script setup lang="ts">
import { computed, ref } from 'vue'
import { useDropZone } from '@vueuse/core'
import { VIcon, VLoading } from '@/modules/shared/components'

const props = withDefaults(
  defineProps<{
    state: 'default' | 'uploading'
  }>(),
  { state: 'uploading' },
)

const dropZoneRef = ref<HTMLDivElement>()
const { isOverDropZone } = useDropZone(dropZoneRef)
const isUploading = computed(() => props.state === 'uploading')
</script>

<template>
  <button
    type="button"
    class="group block w-full cursor-pointer rounded-b-md p-2"
    :class="{
      ' bg-[#3b88af]/10': isOverDropZone,
      'bg-gradient-to-br from-white via-white to-gray-100': !isOverDropZone,
    }"
    draggable
    :aria-busy="isUploading"
    :disabled="isUploading"
    @dragover.prevent
    @drop.prevent
    ref="dropZoneRef"
  >
    <div
      class="rounded-lg border-2 border-dashed p-12"
      :class="{
        'border-[#3b88af]/20': isOverDropZone,
        'border-gray-200': !isOverDropZone,
      }"
    >
      <div class="flex items-center justify-center">
        <div class="relative h-12 w-12">
          <!-- loader -->
          <div
            class="absolute inset-0 flex items-center justify-center transition-opacity duration-300"
            :class="isUploading ? 'opacity-100' : 'opacity-0'"
          >
            <VLoading size="24" />
          </div>

          <!-- icons -->
          <div
            class="transition-all"
            :class="isUploading ? 'translate-y-6 scale-75 opacity-0' : 'translate-y-0 opacity-100'"
          >
            <VIcon
              name="ZipTypeDefault"
              size="48"
              class="absolute z-20 block transition-all duration-150 ease-in-out group-hover:scale-110"
              :class="{ 'scale-110': isOverDropZone }"
            />
            <VIcon
              name="EmptyTypeDefault"
              size="48"
              class="absolute -left-1.5 top-1 block -rotate-6 opacity-40 transition-all duration-150 ease-in-out group-hover:scale-100 group-hover:duration-300"
              :class="{ 'scale-100 duration-300': isOverDropZone, 'scale-90 duration-150': !isOverDropZone }"
            />
            <VIcon
              name="EmptyTypeDefault"
              size="48"
              class="absolute -right-3.5 top-1 block rotate-12 opacity-50 transition-all duration-150 ease-in-out group-hover:scale-100 group-hover:duration-300"
              :class="{ 'scale-100 duration-300': isOverDropZone, 'scale-90 duration-150': !isOverDropZone }"
            />
          </div>
        </div>
      </div>

      <p class="mt-3 text-center text-gray-600">Drop files here or <span class="underline">browse</span></p>
    </div>
  </button>
</template>
